export type BuyerTier = "bronze" | "prata" | "ouro" | "diamante";

export interface IBuyerScoreCriterion {
    key: "emailVerified" | "phoneVerified" | "accountAge" | "purchases" | "reviews";
    label: string;
    points: number;
    maxPoints: number;
    met: boolean;
    rawValue: number | boolean;
}

export interface IBuyerScore {
    score: number;
    tier: BuyerTier;
    breakdown: IBuyerScoreCriterion[];
}

export interface IBuyerScoreInput {
    emailVerified?: boolean;
    phoneVerified?: boolean;
    createdAt?: Date | null;
    purchasesCount?: number;
    reviewsCount?: number;
}

export const BUYER_SCORE_WEIGHTS = {
    emailVerified: 15,
    phoneVerified: 25,
    accountAge: 15,
    purchases: 30,
    reviews: 15,
};

export const ACCOUNT_AGE_CAP_MONTHS = 12;
export const PURCHASES_CAP = 3;
export const REVIEWS_CAP = 5;

function monthsSince(date: Date | null | undefined) {
    if (!date) {
        return 0;
    }
    const months = (Date.now() - date.getTime()) / (1000 * 60 * 60 * 24 * 30);
    return Math.max(0, months);
}

function scaledPoints(value: number, cap: number, maxPoints: number) {
    return Math.min(value / cap, 1) * maxPoints;
}

export function getTierForScore(score: number): BuyerTier {
    if (score >= 85) {
        return "diamante";
    }
    if (score >= 65) {
        return "ouro";
    }
    if (score >= 40) {
        return "prata";
    }
    return "bronze";
}

export function calculateBuyerScore(input: IBuyerScoreInput): IBuyerScore {

    const accountAgeMonths = monthsSince(input.createdAt);
    const purchasesCount = input.purchasesCount || 0;
    const reviewsCount = input.reviewsCount || 0;

    const breakdown: IBuyerScoreCriterion[] = [
        {
            key: "emailVerified",
            label: "E-mail verificado",
            points: input.emailVerified ? BUYER_SCORE_WEIGHTS.emailVerified : 0,
            maxPoints: BUYER_SCORE_WEIGHTS.emailVerified,
            met: !!input.emailVerified,
            rawValue: !!input.emailVerified,
        },
        {
            key: "phoneVerified",
            label: "Telefone verificado",
            points: input.phoneVerified ? BUYER_SCORE_WEIGHTS.phoneVerified : 0,
            maxPoints: BUYER_SCORE_WEIGHTS.phoneVerified,
            met: !!input.phoneVerified,
            rawValue: !!input.phoneVerified,
        },
        {
            key: "accountAge",
            label: "Tempo de conta",
            points: scaledPoints(accountAgeMonths, ACCOUNT_AGE_CAP_MONTHS, BUYER_SCORE_WEIGHTS.accountAge),
            maxPoints: BUYER_SCORE_WEIGHTS.accountAge,
            met: accountAgeMonths >= ACCOUNT_AGE_CAP_MONTHS,
            rawValue: Math.floor(accountAgeMonths),
        },
        {
            key: "purchases",
            label: "Compras concluídas",
            points: scaledPoints(purchasesCount, PURCHASES_CAP, BUYER_SCORE_WEIGHTS.purchases),
            maxPoints: BUYER_SCORE_WEIGHTS.purchases,
            met: purchasesCount >= PURCHASES_CAP,
            rawValue: purchasesCount,
        },
        {
            key: "reviews",
            label: "Avaliações feitas",
            points: scaledPoints(reviewsCount, REVIEWS_CAP, BUYER_SCORE_WEIGHTS.reviews),
            maxPoints: BUYER_SCORE_WEIGHTS.reviews,
            met: reviewsCount >= REVIEWS_CAP,
            rawValue: reviewsCount,
        },
    ];

    const score = Math.round(breakdown.reduce((tot, item) => tot + item.points, 0));

    return {
        score,
        tier: getTierForScore(score),
        breakdown,
    };
}
